import { Drawer as BaseDrawer } from '@base-ui/react/drawer';
import styled from '@emotion/styled';

import { scrollbarStyles } from '../../scrollbar';
import { spacing } from '../../spacing';
import { IconButton } from '../Button';

const forwardProp = (prop: string) => !prop.startsWith('$');

export const StyledBackdrop = styled(BaseDrawer.Backdrop)(({ theme }) => ({
  position: 'fixed',
  minHeight: '100dvh',
  inset: 0,
  backgroundColor: theme.colors.black[100],
  opacity: 0.5,
  transition: 'opacity 150ms cubic-bezier(0.45, 1.005, 0, 1.005)',
  touchAction: 'none',

  '&[data-starting-style], &[data-ending-style]': {
    opacity: 0,
  },
}));

/** Pins the sheet to the bottom edge; `Drawer`'s viewport pins to the trailing edge instead. */
export const StyledViewport = styled(BaseDrawer.Viewport)({
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
});

export const StyledPopup = styled(BaseDrawer.Popup, { shouldForwardProp: forwardProp })<{
  $maxHeight?: number | string;
}>(({ theme, $maxHeight }) => ({
  display: 'flex',
  boxSizing: 'border-box',
  width: '100%',
  // The design has no desktop frame — a sheet is a mobile pattern. Capped and centred so it
  // stays a sheet on a wide monitor instead of becoming a 2560px-wide bar in Storybook.
  maxWidth: 640,
  maxHeight: $maxHeight ?? '90dvh',
  paddingInline: spacing[16],
  paddingBottom: spacing[24],
  paddingTop: 0,
  borderTopLeftRadius: theme.radius[24],
  borderTopRightRadius: theme.radius[24],
  backgroundColor: theme.colors.background.raised,
  outline: 'none',
  touchAction: 'none',
  // No clip margin here on purpose: the margin would widen the clip region and let content
  // paint over the rounded top corners. Nothing focusable reaches this edge — the 16px inset
  // padding is the slack — and `SheetContent` handles rings for its own scrolling children.
  overflow: 'clip',
  transform: 'translateY(var(--drawer-swipe-movement-y))',
  transition: 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)',

  '&[data-starting-style], &[data-ending-style]': {
    transform: 'translateY(100%)',
  },
}));

export const StyledContent = styled(BaseDrawer.Content)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  minHeight: 0,
  gap: spacing[16],
});

/**
 * The grab affordance. Decorative — the popup itself listens for the swipe, so this carries no
 * handler and is hidden from assistive tech, which gets the Close button instead.
 */
export const DragIndicator = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: spacing[8],
  width: '100%',
  flexShrink: 0,
  cursor: 'grab',
});

export const Handle = styled('div')(({ theme }) => ({
  width: 36,
  height: 4,
  // `radius.full` is `50%`, which on a 36x4 box draws an ellipse rather than a pill. Half the
  // height is the pill, and 2 is a real token.
  borderRadius: theme.radius[2],
  backgroundColor: theme.colors.background.overlay,
}));

export const Header = styled('div', { shouldForwardProp: forwardProp })<{ $centered: boolean }>(
  ({ $centered }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[8],
    width: '100%',
    flexShrink: 0,
    ...($centered && { textAlign: 'center' }),
  }),
);

export const SheetContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  overflowY: 'auto',
  flex: 1,
  minHeight: 0,
  // Bleeds 4px in every direction so a focus ring on a full-width child is not sliced by this
  // scroll container. A vertical scroller cannot take `overflow-clip-margin`, so the padding
  // is the slack and the negative margin puts the box back where it was — same trick as
  // `Drawer`'s `PopupContent`.
  padding: 4,
  margin: -4,
  width: 'calc(100% + 8px)',
  ...scrollbarStyles,
});

export const Actions = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'center',
  gap: spacing[8],
  width: '100%',
  flexShrink: 0,
  marginTop: spacing[8],
});

export const ActionRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: spacing[8],
  width: '100%',
});

/**
 * A bottom sheet is only ever reached by touch, so its close control keeps a
 * finger-sized target: the design system's `xs` IconButton is 32px across.
 */
export const CloseButton = styled(IconButton)({
  '&&': { padding: spacing[16] },
});
